import { useEffect, useRef, useCallback, Fragment } from 'react';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {bindActionCreators} from "redux";
import PropTypes from 'prop-types';
import { faCalendar } from "@fortawesome/free-solid-svg-icons";
import { Button } from "reactstrap";
import { CalendarComponent } from '@syncfusion/ej2-react-calendars';
import PerfectScrollbar from 'react-perfect-scrollbar';
import 'react-perfect-scrollbar/dist/css/styles.css';

import SideBarIcon from '../../../assets/icons/calendar/sidebar.svg?react';
import AddSquareIcon from '../../../assets/icons/icon-add-square.svg?react';
import CalendarItem from "./calendaritem/calendaritem";
import { selectCalendar, checkCalendar } from "../../../calendar/components/sidebar/sidebar.actions";
import '../../../styles/main.scss';
import './side-bar.scss';


const SideBar = (props) => {
  const { 
    collapsed,
    calendarResult,
    isIframeContainer,
    sideBarToggle,
    onCalendarChange,
    selectedDate,
    onCalendarOpenEditor,
    onCalendarOpenCalnendarView,
    onCalendarClick,
    onCalendarDelete,
    onCalendarColorModify
  } = props;
  const { t } = useTranslation();
  const scrollbarRef = useRef(null);

  useEffect(() => {
    const checkScrollbarHeight = () => {
      if (scrollbarRef.current) {
        const container = scrollbarRef.current._container;
        if (container) {
          const rail = container.querySelector('.ps__rail-y');
          if (rail) {
            const containerHeight = container.offsetHeight;
            if (containerHeight < 500) {
              rail.style.display = 'none';
            } else {
              rail.style.display = '';
            }
          }
        }
      }
    };

    checkScrollbarHeight();
    window.addEventListener('resize', checkScrollbarHeight);

    return () => {
      window.removeEventListener('resize', checkScrollbarHeight);
    };
  }, [collapsed, calendarResult]);

  const newEventClick = useCallback(() => {
    if (props.isIframeContainer) {
      const lexon = props.lexon.lexonToken ? props.lexon.lexonToken : props.lexon.user;
      if (props.lexon.lexonToken) {
        window.open(`calendar/access/${lexon}/?prov=IM0`, "_blank");
      } else if (props.lexon.user) {
        window.open(`calendar/user/${lexon}`, "_blank");
      } else {
        window.open("calendar", "_blank");
      }
    } else {
      onCalendarOpenEditor();
    }
  }, [props, onCalendarOpenEditor]);

  const newCalendarClick = useCallback(() => {
    onCalendarOpenCalnendarView();
  }, [onCalendarOpenCalnendarView]);

  const navigateToList = useCallback((evt, calendarId, checked) => {
    const calendar = calendarResult.calendars.find(el => el.id === calendarId);
    props.selectCalendar(calendar.d, checked);
    props.checkCalendar(calendar.id, checked);
    onCalendarClick(calendar.id, checked);
  }, [calendarResult, props, onCalendarClick]);

  const calendarChange = useCallback((args) => {
    onCalendarChange(args);
  }, [onCalendarChange]);

  return (
    <nav
      id={isIframeContainer ? 'left-sidebar-iframe' : 'left-sidebar'}
      className={
        collapsed
          ? `d-flex flex-column text-truncate left-panel-calendar p-sidebar-panel sidebar-close`
          : `d-flex flex-column text-truncate left-panel-calendar p-sidebar-panel sidebar-open-calendar`
      }>
      <PerfectScrollbar
        ref={scrollbarRef}
        component="ul"
        className="d-flex flex-column border-0 m-0 sidebar">
        <div className="compose-panel-calendar">
          <div className="d-flex align-items-center justify-content-between mb-2">
            <span className="text-color-blue-lefebvre inter-medium-14">
              { isIframeContainer && t('calendar-sidebar.calendar-view')}
            </span>
            <Button
              onClick={sideBarToggle}
              className={`btn-transparent`}>
               <SideBarIcon className="side-bar-icon-close" />
            </Button>
          </div>
          {!isIframeContainer && (
            <div className="d-flex align-items-center justify-content-between add-event-container">
              <span className="inter-semibold-16 text-color-blue-lefebvre">
                {t('calendar-sidebar.events')}
              </span>
              <Button
                onClick={newEventClick}
                className={`btn-transparent`}>
                  <AddSquareIcon className="icon-add-square" />
              </Button>
            </div>
          )}
        </div>
        {!isIframeContainer && (
            <>
            <div
              className='calendar-control-section selected-calendar-container'
              style={{ overflow: 'initial', innerWidth: '40%', Height: '40%' }}
            >
              <CalendarComponent
                change={calendarChange}
                value={selectedDate}
                firstDayOfWeek={1}
                dayHeaderFormat="Abbreviated"
              />
            </div>
            <div className="d-flex align-items-center justify-content-between new-calendar-container">
              <span className="inter-semibold-16 text-color-blue-lefebvre">
                {t("calendar-sidebar.showCalendars")}
              </span>
              <Button
                onClick={newCalendarClick}
                className={`btn-transparent`}
              >
                <AddSquareIcon className="icon-add-square" />
              </Button>
            </div>
            </>
        )}
        <Fragment>
          <div className="type-calendars-container mb-3">
            <span className="text-color-blue-lefebvre inter-medium-14">{
              t("calendar-sidebar.mycalendars")
            }</span>
          </div>
          { calendarResult.calendars.length > 0 ?
            calendarResult.calendars.map(el => {
              const iconProps = {
                icon: faCalendar,
                color: "#001978",
                size: "lg"
              };
              return (
                <CalendarItem
                  key={`${el.id}_label`}
                  onClick={navigateToList}
                  name={el.summary}
                  id={el.id}
                  color={el.backgroundColor}
                  accessRole={el.accessRole}
                  iconProps={iconProps}
                  selected={el.checked}
                  primary={(el.summary === "Personal")}
                  onCalendarOpenCalnendarView={props.onCalendarOpenCalnendarView}
                  onCalendarDelete={onCalendarDelete}
                  onCalendarColorModify={onCalendarColorModify}
                  isIframeContainer={props.isIframeContainer}
                />
              );
            }) : null}
        </Fragment>
      </PerfectScrollbar>
    </nav>
  );
}


SideBar.propTypes = {
  sideBarToggle: PropTypes.func.isRequired,
  collapsed: PropTypes.bool.isRequired,
  application: PropTypes.object,
  errors: PropTypes.object,
  calendarResult: PropTypes.object.isRequired,
  isIframeContainer: PropTypes.bool,
  onCalendarChange: PropTypes.func,
  selectedDate: PropTypes.any,
  onCalendarOpenEditor: PropTypes.func,
  onCalendarOpenCalnendarView: PropTypes.func,
  onCalendarClick: PropTypes.func,
  onSidebarCloseClick: PropTypes.func,
  onCalendarDelete: PropTypes.func,
  onCalendarColorModify: PropTypes.func,
  selectCalendar: PropTypes.func,
  checkCalendar: PropTypes.func,
  lexon: PropTypes.object
};

const mapStateToProps = state => ({
  application: state.application,
  errors: state.application.errors,
  lexon: state.lexon
});

const mapDispatchToProps = dispatch => ({
  ...bindActionCreators(
    {
      selectCalendar,
      checkCalendar
    },
    dispatch
  )
});

const mergeProps = (stateProps, dispatchProps, ownProps) =>
  Object.assign({}, stateProps, dispatchProps, ownProps, {
    moveFolderToFirstLevel: folder =>
      dispatchProps.moveFolderToFirstLevel(stateProps.application.user, folder)
  });

export default connect(
  mapStateToProps,
  mapDispatchToProps,
  mergeProps
)(SideBar);
